import { useEffect, useRef } from "react";
import {
  drag as d3Drag,
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  forceRadial,
  select,
  zoom as d3Zoom,
  zoomIdentity,
} from "d3";

import {
  buildGraph,
  getNodeRadius,
  getBrandInitials,
  navigateToBrandPage,
} from "./brandRadarUtils";

export default function useBrandRadarGraph({
  svgRef,
  dimensions,
  loading,
  currentCenter,
  relations,
  dirSim,
  adj,
  brandLogos = {},
  isMobile,
  navigateToBrandRef,
  onStatsChange,
  onSelectBrand,
}) {
  const simulationRef = useRef(null);
  const zoomRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0 || loading) return;

    const svg = select(svgRef.current);
    const { width, height } = dimensions;

    // Clear previous content
    svg.selectAll("*").remove();

    const mainGroup = svg.append("g");

    // Setup zoom
    const zoom = d3Zoom()
      .scaleExtent([0.1, 5])
      .on("zoom", (e) => mainGroup.attr("transform", e.transform));
    svg.call(zoom);

    // Store zoom reference for controls
    zoomRef.current = zoom;

    // Build graph
    const data = buildGraph(currentCenter, relations, dirSim, adj, brandLogos);
    onStatsChange({ nodes: data.nodes.length, links: data.links.length });

    // Set initial selected brand
    onSelectBrand({ id: data.nodes[0].id });

    // Links - start with opacity 0 for fade-in animation
    const linkElements = mainGroup
      .append("g")
      .selectAll("line")
      .data(data.links)
      .join("line")
      .attr("class", "link")
      .attr("stroke", (d) =>
        d.level <= 1 ? "rgba(200,255,0,.2)" : "rgba(200,255,0,.045)",
      )
      .style("opacity", 0);

    // Fade in links
    linkElements
      .transition()
      .duration(800)
      .delay((d, i) => i * 5)
      .style("opacity", 1);

    // Nodes - start with opacity 0 for fade-in animation
    const nodeElements = mainGroup
      .append("g")
      .selectAll("g")
      .data(data.nodes)
      .join("g")
      .attr("class", "node")
      .style("opacity", 0)
      .call(
        d3Drag()
          .on("start", (e, d) => {
            if (!e.active) simulationRef.current.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on("drag", (e, d) => {
            d.fx = e.x;
            d.fy = e.y;
          })
          .on("end", (e, d) => {
            if (!e.active) simulationRef.current.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          }),
      );

    // Fade in nodes with staggered delay based on level
    nodeElements
      .transition()
      .duration(600)
      .delay((d) => {
        if (d.level === 0) return 100;
        if (d.level === 1) return 300 + Math.random() * 200;
        return 500 + Math.random() * 300;
      })
      .style("opacity", 1);

    const getButtonOffsetY = (d) => -getNodeRadius(d, isMobile) - 20;
    const getLogoSize = (d) => {
      return Math.max((getNodeRadius(d, isMobile) - 2) * 2, 12);
    };
    const getFallbackLogoFontSize = (d) => {
      const scale = isMobile ? 0.85 : 1;
      if (d.level === 0) return `${17 * scale}px`;
      if (d.level === 1) return `${12 * scale}px`;
      return `${8 * scale}px`;
    };

    // Larger invisible target keeps node hover active while moving to the CTA.
    nodeElements
      .append("rect")
      .attr("class", "hit-area")
      .attr("x", -60)
      .attr("y", (d) => -getNodeRadius(d, isMobile) - 30)
      .attr("width", 120)
      .attr("height", (d) => getNodeRadius(d, isMobile) + 40);

    // Ring
    nodeElements
      .append("circle")
      .attr("class", "ring")
      .attr("r", (d) => getNodeRadius(d, isMobile))
      .attr("stroke", (d) => {
        if (d.level === 0) return "#c8ff00";
        if (d.level === 1) return "rgba(200,255,0,.35)";
        return "rgba(255,255,255,.07)";
      });

    // Fill
    nodeElements
      .append("circle")
      .attr("r", (d) => getNodeRadius(d, isMobile) - 1.5)
      .attr("fill", (d) => {
        if (d.level === 0) return "#c8ff00";
        if (d.level === 1) return "rgba(200,255,0,.07)";
        return "rgba(255,255,255,.025)";
      });

    // Glow for center node
    nodeElements
      .filter((d) => d.level === 0)
      .append("circle")
      .attr("r", isMobile ? 40 : 50)
      .attr("fill", "none")
      .attr("stroke", "#c8ff00")
      .attr("stroke-width", 1.5)
      .attr("opacity", 0.12)
      .style("animation", "BrandRadar-glow-pulse 3s ease-in-out infinite");

    // Brand logos
    const logoElements = nodeElements.filter((d) => Boolean(d.logo));

    logoElements
      .append("foreignObject")
      .attr("class", "brand-logo-object")
      .attr("x", (d) => -getLogoSize(d) / 2)
      .attr("y", (d) => -getLogoSize(d) / 2)
      .attr("width", (d) => getLogoSize(d))
      .attr("height", (d) => getLogoSize(d))
      .append("xhtml:div")
      .attr("class", "brand-logo-frame")
      .append("xhtml:img")
      .attr("src", (d) => d.logo)
      .attr("alt", (d) => d.id);

    // Logo-like initials fallback
    nodeElements
      .filter((d) => !d.logo)
      .append("foreignObject")
      .attr("class", "brand-logo-object brand-logo-object--fallback")
      .attr("x", (d) => -getLogoSize(d) / 2)
      .attr("y", (d) => -getLogoSize(d) / 2)
      .attr("width", (d) => getLogoSize(d))
      .attr("height", (d) => getLogoSize(d))
      .append("xhtml:div")
      .attr(
        "class",
        (d) =>
          `brand-logo-frame brand-logo-frame--fallback brand-logo-frame--level-${d.level}`,
      )
      .append("xhtml:span")
      .attr("class", "brand-logo-fallback-text")
      .style("font-size", (d) => getFallbackLogoFontSize(d))
      .text((d) => getBrandInitials(d.id));

    // Label
    nodeElements
      .append("text")
      .attr("class", "lbl")
      .attr("dy", (d) => getNodeRadius(d, isMobile) + 13)
      .attr("font-size", (d) => {
        const scale = isMobile ? 0.9 : 1;
        if (d.level === 0) return `${12 * scale}px`;
        if (d.level === 1) return `${10 * scale}px`;
        return `${8 * scale}px`;
      })
      .attr("fill", (d) => {
        if (d.level === 0) return "#c8ff00";
        if (d.level === 1) return "rgba(232,228,223,.55)";
        return "rgba(232,228,223,.22)";
      })
      .text((d) => (d.id.length > 22 ? d.id.substring(0, 20) + "..." : d.id));

    // "View on Fruits" button
    const buttonGroup = nodeElements
      .append("g")
      .attr("class", "btn-g")
      .attr("transform", (d) => `translate(0,${getButtonOffsetY(d)})`);

    buttonGroup
      .append("rect")
      .attr("class", "btn-bg")
      .attr("x", -56)
      .attr("y", -12)
      .attr("width", 112)
      .attr("height", 24)
      .attr("rx", 6);

    buttonGroup
      .append("text")
      .attr("class", "btn-text")
      .attr("dy", ".35em")
      .text("View on Fruits");

    buttonGroup.on("click", function (e, d) {
      e.stopPropagation();
      e.preventDefault();
      navigateToBrandPage(d.id);
    });

    // Hover highlight
    nodeElements.on("pointerenter", (e, d) => {
      const connected = new Set([d.id]);
      data.links.forEach((l) => {
        const s = l.source.id || l.source;
        const t = l.target.id || l.target;
        if (s === d.id) connected.add(t);
        if (t === d.id) connected.add(s);
      });
      nodeElements.classed("is-dim", (n) => !connected.has(n.id));
      linkElements.classed("is-dim", (l) => {
        const s = l.source.id || l.source;
        const t = l.target.id || l.target;
        return s !== d.id && t !== d.id;
      });
      linkElements.classed("is-highlight", (l) => {
        const s = l.source.id || l.source;
        const t = l.target.id || l.target;
        return s === d.id || t === d.id;
      });
    });

    nodeElements.on("pointerleave", () => {
      nodeElements.classed("is-dim", false);
      linkElements.classed("is-dim", false).classed("is-highlight", false);
    });

    // Click
    nodeElements.on("click", function (e, d) {
      e.stopPropagation();
      if (navigateToBrandRef.current) {
        navigateToBrandRef.current(d.id);
      }
    });

    // Double click to open brand page
    nodeElements.on("dblclick", (e, d) => {
      e.stopPropagation();
      e.preventDefault();
      navigateToBrandPage(d.id);
    });

    // Force simulation
    const simulation = forceSimulation(data.nodes)
      .alpha(1)
      .alphaDecay(0.02)
      .velocityDecay(0.3)
      .force(
        "link",
        forceLink(data.links)
          .id((d) => d.id)
          .distance((d) => {
            const minLevel = Math.min(
              typeof d.source === "object" ? d.source.level : 0,
              typeof d.target === "object" ? d.target.level : 0,
            );
            const scale = isMobile ? 0.7 : 1;
            if (minLevel === 0) return 220 * scale;
            if (minLevel === 1) return 160 * scale;
            return 100 * scale;
          })
          .strength((d) => (d.level <= 1 ? 0.6 : 0.2)),
      )
      .force(
        "charge",
        forceManyBody().strength((d) => {
          const scale = isMobile ? 0.6 : 1;
          if (d.level === 0) return -1400 * scale;
          if (d.level === 1) return -500 * scale;
          return -150 * scale;
        }),
      )
      .force("center", forceCenter(width / 2, height / 2))
      .force(
        "collision",
        forceCollide().radius((d) => getNodeRadius(d, isMobile) + 14),
      )
      .force(
        "radial",
        forceRadial(
          (d) => {
            const scale = isMobile ? 0.6 : 1;
            if (d.level === 0) return 0;
            if (d.level === 1) return 220 * scale;
            return 420 * scale;
          },
          width / 2,
          height / 2,
        ).strength((d) => (d.level === 0 ? 1 : 0.2)),
      )
      .on("tick", () => {
        linkElements
          .attr("x1", (d) => d.source.x)
          .attr("y1", (d) => d.source.y)
          .attr("x2", (d) => d.target.x)
          .attr("y2", (d) => d.target.y);
        nodeElements.attr("transform", (d) => `translate(${d.x},${d.y})`);
      });

    simulationRef.current = simulation;

    // Initial zoom
    const initialScale = isMobile ? 0.6 : 0.8;
    svg
      .transition()
      .duration(500)
      .call(
        zoom.transform,
        zoomIdentity
          .translate(width / 2, height / 2)
          .scale(initialScale)
          .translate(-width / 2, -height / 2),
      );

    // Click on empty space to hide info panel
    svg.on("click", () => onSelectBrand(null));

    return () => {
      simulation.stop();
    };
  }, [
    currentCenter,
    dimensions,
    dirSim,
    adj,
    brandLogos,
    isMobile,
    loading,
    relations,
  ]);

  return { zoomRef };
}
